/**
 * Theme Dependency Fixes
 *
 * Provides missing utility functions that the theme expects but are undefined.
 * These utilities are referenced by multiple theme scripts but missing from global.js
 * or were not loaded properly.
 */
(function() {
  'use strict';

  const DEBUG = window.location.search.includes('theme_debug=true');

  function log(...args) {
    if (DEBUG) {
      console.log('[Theme Fixes]', ...args);
    }
  }

  log('Loading theme dependency fixes...');

  // Prevent duplicate initialization
  if (window.__themeDependenciesFixed) {
    log('Already initialized, skipping');
    return;
  }

  /**
   * Debounce utility - Limits how often a function can run
   * Used by: media-gallery.s.min.js, search-form.s.min.js, quick-order-list.js
   *
   * Standard Shopify/Lodash-style debounce with immediate option
   *
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Milliseconds to wait
   * @param {boolean} immediate - If true, trigger on leading edge instead of trailing
   * @returns {Function} - Debounced function
   */
  function debounce(fn, wait, immediate = false) {
    let timeout;
    return function executedFunction(...args) {
      const context = this;
      const later = () => {
        timeout = null;
        if (!immediate) fn.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) fn.apply(context, args);
    };
  }

  /**
   * Throttle utility - Ensures function runs at most once per interval
   *
   * @param {Function} fn - Function to throttle
   * @param {number} wait - Milliseconds between allowed calls
   * @returns {Function} - Throttled function
   */
  function throttle(fn, wait) {
    let inThrottle;
    let lastFn;
    let lastTime;

    return function(...args) {
      const context = this;

      if (!inThrottle) {
        fn.apply(context, args);
        lastTime = Date.now();
        inThrottle = true;
      } else {
        clearTimeout(lastFn);
        lastFn = setTimeout(() => {
          if (Date.now() - lastTime >= wait) {
            fn.apply(context, args);
            lastTime = Date.now();
          }
        }, Math.max(wait - (Date.now() - lastTime), 0));
      }
    };
  }

  /**
   * FetchConfig utility - Standard fetch configuration
   * Used by: Various theme scripts for API calls
   *
   * @param {string} type - Request type (json, javascript, text)
   * @returns {Object} - Fetch configuration object
   */
  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/${type}`
      }
    };
  }

  /**
   * Subscribe utility - Pub/sub pattern for theme events
   * Used by: product-info.s.min.js
   *
   * @param {string} channel - Event channel name
   * @param {Function} callback - Function to call when event fires
   */
  const PubSub = {
    subscribers: {},

    subscribe: function(channel, callback) {
      if (!this.subscribers[channel]) {
        this.subscribers[channel] = [];
      }
      this.subscribers[channel].push(callback);

      // Return unsubscribe function
      return () => {
        this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      };
    },

    publish: function(channel, data) {
      if (!this.subscribers[channel]) return;
      this.subscribers[channel].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[Theme Fixes] Error in subscriber for ${channel}:`, error);
        }
      });
    }
  };

  /**
   * DetailsDisclosure - Disclosure widget class
   * Used by: share.s.min.js
   */
  class DetailsDisclosure extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');

      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
      }
    }

    onToggle() {
      if (!this.mainDetailsToggle.open) return;

      // Close other disclosure elements in the same group
      const group = this.dataset.group;
      if (group) {
        const others = document.querySelectorAll(`details-disclosure[data-group="${group}"] details[open]`);
        others.forEach(details => {
          if (details !== this.mainDetailsToggle) {
            details.removeAttribute('open');
          }
        });
      }
    }
  }

  /**
   * ModalDialog - Modal dialog class
   * Used by: product-modal.s.min.js
   */
  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
      this.addEventListener('keyup', (event) => {
        if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
      });

      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointerup', (event) => {
          if (event.target === this) this.hide();
        });
      } else {
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.removeAttribute('aria-hidden');

      // Focus first focusable element or the modal itself
      const focusElement = this.querySelector('[autofocus]') ||
                          this.querySelector('button') ||
                          this;
      focusElement.focus();
    }

    hide(focusToggle = true) {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');

      if (focusToggle && this.openedBy) {
        this.openedBy.focus();
      }
    }
  }

  /**
   * ModalOpener - Opens modal dialogs
   * Used by: Various theme scripts
   */
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector('button');
      if (!button) return;

      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }

  /**
   * SearchForm - Search form with reset button
   * Used by: predictive-search.s.min.js (extends this class)
   */
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');

      if (this.input) {
        this.input.form.addEventListener('reset', this.onFormReset.bind(this));
        this.input.addEventListener('input', debounce((event) => {
          this.onChange(event);
        }, 300).bind(this));
      }
    }

    toggleResetButton() {
      if (!this.resetButton) return;

      const isHidden = this.resetButton.classList.contains('hidden');
      if (this.input.value.length > 0 && isHidden) {
        this.resetButton.classList.remove('hidden');
      } else if (this.input.value.length === 0 && !isHidden) {
        this.resetButton.classList.add('hidden');
      }
    }

    onChange() {
      this.toggleResetButton();
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      event.preventDefault();
      if (this.shouldResetForm()) {
        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
      }
    }
  }

  // Register all utilities globally
  window.debounce = debounce;
  window.throttle = throttle;
  window.fetchConfig = fetchConfig;
  window.subscribe = PubSub.subscribe.bind(PubSub);
  window.publish = PubSub.publish.bind(PubSub);

  // Export base classes globally so theme scripts can extend them
  // DO NOT register as custom elements here - let the theme scripts do that
  window.DetailsDisclosure = DetailsDisclosure;
  window.ModalDialog = ModalDialog;
  window.ModalOpener = ModalOpener;
  window.SearchForm = SearchForm;

  log('Exported base classes: DetailsDisclosure, ModalDialog, ModalOpener, SearchForm');

  // Mark as initialized
  window.__themeDependenciesFixed = true;

  log('✅ Theme dependency fixes loaded successfully');
  log('Available utilities: debounce, throttle, fetchConfig, subscribe, publish');
  log('Available base classes: DetailsDisclosure, ModalDialog, ModalOpener, SearchForm');

})();
